import { createEvent, createStore, Effect, EventCallable, restore, sample } from 'effector';
import { modelFactory } from 'effector-factorio';
import { createGate } from 'effector-react';

import { notifyFromEffect } from 'shared/notifier';

import { getCurrentTodosFx, getPreviousTodosFx } from '../model/effects';

type EffectParams = {
  task_id: string;
};

type Props<P extends EffectParams, Done, Error> = {
  postEffect: Effect<P, Done, Error>;
  onCloseTodoList?: EventCallable<void>;
};

export const factory = modelFactory(
  <P extends EffectParams, Done, Error>({ postEffect, onCloseTodoList }: Props<P, Done, Error>) => {
    const TodoListGate = createGate();

    const $todos = restore(getCurrentTodosFx.doneData, null);

    const completeTask = createEvent<P>();

    const $completedTask = restore(completeTask, null).reset(postEffect.done);

    sample({
      clock: completeTask,
      target: postEffect,
    });

    sample({
      clock: TodoListGate.open,
      source: $todos,
      filter: ($todos) => !$todos,
      target: getCurrentTodosFx,
    });

    sample({
      clock: postEffect.doneData,
      target: [getCurrentTodosFx, getPreviousTodosFx],
    });

    notifyFromEffect({
      effect: postEffect,
      $isActive: TodoListGate.status,
      isNeedRedirect: false,
      error: 'Эта задача уже закрыта',
      success: 'Задача успешно закрыта',
    });

    const checkPreviousTodos = createEvent();
    const hidePreviousTodos = createEvent();
    const $isPreviousTodosVisible = createStore(false)
      .on(checkPreviousTodos, (state) => !state)
      .on(hidePreviousTodos, (state) => !state);

    const $previousTodos = restore(getPreviousTodosFx.doneData, null);

    sample({
      clock: checkPreviousTodos,
      source: $previousTodos,
      filter: (todos) => !todos || todos?.tasks.length === 0,
      target: getPreviousTodosFx,
    });

    if (onCloseTodoList) {
      sample({
        clock: TodoListGate.close,
        target: onCloseTodoList,
      });
    }

    return {
      TodoListGate,
      $todos,
      completeTask,
      checkPreviousTodos,
      hidePreviousTodos,
      $isPreviousTodosVisible,
      $previousTodos,
      $completedTask,
    };
  }
);
